import { useEffect } from "react";

/**
 * After MathJax typesets, it injects inline font-size on every mjx-container
 * (e.g. style="font-size: 123.4%") and inner mjx-mstyle may carry mathsize
 * scaling too. We strip those inline sizes so the equations inherit the
 * surrounding text size via CSS instead.
 */
export const stripMathJaxInlineSizes = () => {
  document.querySelectorAll("mjx-container").forEach((el) => {
    el.style.removeProperty("font-size");
  });
  document.querySelectorAll("mjx-container mjx-mstyle").forEach((el) => {
    el.style.removeProperty("font-size");
  });
};

export const typesetMathJaxElements = (elements) => {
  const nodes = (Array.isArray(elements) ? elements : [elements]).filter(Boolean);
  if (!nodes.length) {
    return Promise.resolve();
  }

  const run = () => {
    try {
      if (window.MathJax?.typesetClear) {
        window.MathJax.typesetClear(nodes);
      }
      if (window.MathJax?.typesetPromise) {
        return window.MathJax.typesetPromise(nodes).then(stripMathJaxInlineSizes);
      }
      if (window.MathJax?.typeset) {
        window.MathJax.typeset(nodes);
        stripMathJaxInlineSizes();
        return Promise.resolve();
      }
    } catch (e) {
      console.warn("MathJax typeset error:", e);
    }
    return Promise.resolve();
  };

  if (window.MathJax?.typeset || window.MathJax?.typesetPromise) {
    return run();
  }

  if (window.MathJax?.startup?.promise) {
    return window.MathJax.startup.promise.then(run);
  }

  return Promise.resolve();
};

const loadMathJaxScript = () => {
  if (document.getElementById("mathjax-script")) {
    return;
  }

  window.MathJax = {
    tex: {
      inlineMath: [["$", "$"], ["\\(", "\\)"]],
      displayMath: [["$$", "$$"], ["\\[", "\\]"]],
      processEscapes: true,
    },
    options: {
      skipHtmlTags: ["script", "noscript", "style", "textarea", "pre"],
    },
    startup: {
      ready() {
        window.MathJax.startup.defaultReady();
        window.MathJax.startup.promise.then(stripMathJaxInlineSizes);
      },
    },
  };

  const script = document.createElement("script");
  script.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js";
  script.async = true;
  script.id = "mathjax-script";
  script.onerror = () => {
    console.warn("MathJax failed to load from CDN.");
  };
  document.body.appendChild(script);
};

const useMathjax = (dependency, dependency1) => {
  useEffect(() => {
    loadMathJaxScript();

    const timer = setTimeout(() => {
      typesetMathJaxElements(document.body);
    }, 0);

    return () => clearTimeout(timer);
  }, [dependency, dependency1]);
};

export default useMathjax;
